import classnames from "classnames";
import classNames from "classnames";
import React, {
    Children,
    ReactElement,
    cloneElement,
    useEffect,
    useState,
} from "react";

import { KeyNames } from "@utils/enums";
import { createKeyHandler } from "@utils/keyboard";

import "./headerButton.scss";

export type HeaderButtonProps = {
    children?: React.ReactNode;
    icon: string;
    iconPrefix?: string;
    text?: string;
    hover?: boolean; // Use hover as a trigger
    alerts?: number | string;
    invert?: boolean;
    addCloseCallback?: boolean;
    link?: string;
    large?: Boolean;
    hiddenText?: boolean;
    ariaLabel?: string;
    contentClassName?: string;
};

const HeaderButton = ({
    icon,
    alerts,
    text,
    children,
    addCloseCallback,
    hover,
    invert,
    link,
    large,
    iconPrefix = "las",
    hiddenText = false,
    ariaLabel,
    contentClassName
}: HeaderButtonProps) => {
    const [isActive, setActiveState] = useState(false);
    const [preventBlur, setPreventBlur] = useState(false);

    useEffect(() => {
        const handleOnKeyDown = createKeyHandler([
            {
                keys: [KeyNames.ESCAPE],
                handler: () => setActive(false),
            },
        ]);

        if (typeof window !== "undefined") {
            window?.addEventListener("header-button-activated", () => {
                setActive(false);
            });
            window.addEventListener("keydown", handleOnKeyDown);
        }
        return function () {
            if (typeof window !== "undefined") {
                window?.removeEventListener("header-button-activated", () => {
                    setActive(false);
                });
                window.removeEventListener("keydown", handleOnKeyDown);
            }
        };
    }, []);

    const setActive = (active: boolean = true) => {
        if (active) {
            if (typeof window !== "undefined") {
                window?.dispatchEvent(new Event("header-button-activated"));
            }
        }

        setActiveState(active);
    };

    const onMouseEnter = () => {
        if (hover && children) {
            setActive();
        }
        setPreventBlur(true);
    };

    const onMouseLeave = () => {
        if (hover && children) {
            setActive(false);
        }
        setPreventBlur(false);
    };

    const onClick: React.MouseEventHandler<HTMLAnchorElement> = e => {
        if (!hover && children) {
            e.preventDefault();
            setActive(!isActive);
            setPreventBlur(true);
        }
    };

    const onBlur = () => {
        if (!preventBlur) {
            setActive(false);
        }
    };

    const closePopupWindow = () => {
        setActive(false);
        setPreventBlur(false);
    };

    const childElement =
        addCloseCallback && children
            ? cloneElement(Children.only(children as ReactElement), {
                  closeCallback: closePopupWindow,
              })
            : children;

    const buttonStateStyling = [
        invert ? "header-button__element--invert" : "",
        isActive ? "header-button__element--active" : "",
    ].join(" ");

    return (
        <div
            className="header-button"
            data-test={`header-button-${text}`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onBlur={onBlur}
        >
            <a
                className={`h4 header-button__element ${buttonStateStyling}`}
                data-test={`header-button-${text}-button`}
                href={link}
                onClick={onClick}
                aria-label={ariaLabel}
            >
                <i
                    className={`header-button__icon ${iconPrefix} la-${icon} mr-1 mb-1 align-middle`}
                >
                    {!!alerts && (
                        <span className={"header-button__icon--alerts"}>
                            {alerts}
                        </span>
                    )}
                </i>
                <span
                    className={classNames("d-none d-lg-inline", {
                        "sr-only": hiddenText,
                    })}
                >
                    {text}
                </span>
                {!hover && children && (
                    <i
                        className={classnames([
                            "header-button__chevron",
                            `las la-angle-down`,
                            isActive && "header-button__chevron--active",
                        ])}
                    />
                )}
            </a>
            <div
                className={classnames(
                    "header-button__content border rounder shadow bg-white",
                    [
                        isActive && "header-button__content--active",
                        large && "header-button__content--large",
                    ],
                    contentClassName
                )}
            >
                {childElement}
            </div>
        </div>
    );
};

export { HeaderButton };
